/*
  nsi-search-price.js — prices inside the instant-search flydown's LIVE results.

  WHY THIS FILE EXISTS
  --------------------
  The flydown has two product surfaces and they render by completely different routes:

    1. "Best sellers" (the empty state, before you type) is Liquid. It calls the shared
       snippets/product-price.liquid, so it already gets the store's normal price treatment.
    2. The LIVE results (after you type) are built client-side by inline JS in
       sections/static-basic-header.liquid off /search/suggest.json. Liquid never runs on
       them, so every row was one flat price in the header magenta - no strike, no markdown,
       no trade line - on a store where the same product is priced three ways in every other
       grid. Worse, magenta reads as the trade colour, so retail shoppers were being shown a
       trade-looking price.

  This file fixes (2) without touching the renderer: it watches the results container and
  rewrites each row's price node in place, for EVERY visitor, not only trade members.

  WHAT EACH VISITOR SEES (the same language as the rest of the store)
  ------------------------------------------------------------------
    Verified trade member, product not excluded, real rate:
        grey struck compare-at / GREEN struck retail / PURPLE bold trade price / TRADE pill
    Everyone else - logged out, logged in without the tag, and trade applicants still
    PENDING - gets the retail treatment, never purple and never a pill:
        markdown:    grey struck compare-at, then the current price in GREEN
        no markdown: the price alone in the flydown's normal dark text
    Green only ever means "this is reduced". A full price is never green.

  Pending applicants fall out as retail by construction: snippets/live-search-form.liquid only
  emits window.NSISearchTrade when snippets/nsi-trade-state.liquid returns 'active', and it
  returns 'pending' for the trade-pending tag and an empty string for every near-miss tag.

  THE RULES IT FOLLOWS (same as snippets/product-price.liquid, deliberately)
  -------------------------------------------------------------------------
  * No rate, no trade line. window.NSISearchTrade is only emitted for a verified customer AND
    a real shop.metafields.nsi.trade_rate. There is no fallback percentage anywhere in here:
    showing a rate BSS is not charging is worse than showing none.
  * Products tagged `no-trade` are excluded, read from the authoritative /products/<handle>.js
    rather than the suggest payload. Anything we cannot read stays at the plain price.
  * COMPARE-AT IS ANCHORED TO THE VARIANT THAT SETS THE DISPLAYED PRICE. The collection cards
    take the struck price from the product's MAXIMUM compare-at while showing the MINIMUM
    variant price (snippets/api-theme.liquid `product:price` assigns product.compare_at_price,
    which Liquid defines as the highest), so a card can advertise "was $165 / from $15" on a
    product whose $15 variants were never discounted at all. That is not reproduced here: the
    struck tier comes from the same variant as the "from" figure, or it does not render.
    The anchor function below is a port of snippets/nsi-price-anchor.liquid, tie-break for
    tie-break, so a row and a card quoting the same product cannot disagree.
*/
(function () {
  'use strict';

  /* product-price.liquid and the range_html locale string both write this lowercase. */
  var FROM = 'from ';
  var pcache = Object.create(null); /* handle -> Promise<product JSON | null> */
  var lines = [];                   /* rendered price lines, re-measured on resize */

  /* ---- state ------------------------------------------------------------- */
  /* Read lazily on every row so the state can change at runtime - nsi-trade-live.js flips a
     customer to active the moment their ABN verifies, without a reload. */
  function tradeState() {
    var g = window.NSISearchTrade;
    var rate = g ? parseFloat(g.rate) : NaN;
    if (!isFinite(rate)) {
      var host = document.querySelector('.live-search[data-nsi-trade-rate]');
      rate = host ? parseFloat(host.getAttribute('data-nsi-trade-rate')) : NaN;
    }
    if (!isFinite(rate) || rate <= 0 || rate >= 100) return null;
    return { rate: rate, badge: (g && g.badge) || 'Trade' };
  }

  /* ---- maths + formatting ------------------------------------------------ */
  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  /* Mirrors `price | times: factor | plus: 50 | divided_by: 100` from product-price.liquid.
     Liquid floors that last step on integers, which is what a whole-number rate hits. */
  function tradeCents(price, rate) {
    var factor = 100 - rate;
    var raw = price * factor + 50;
    return Math.round(rate) === rate ? Math.floor(raw / 100) : Math.round(raw / 100);
  }
  function handleOf(href) {
    var m = /\/products\/([^?#\/]+)/.exec(href || '');
    return m ? m[1] : '';
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* ---- product data ------------------------------------------------------ */
  /* /search/suggest.json carries `tags` but returns an empty `variants` array, and its
     compare_at_price_min/max are the same max-vs-min trap as the collection cards. The
     product JSON is the only source that can anchor the strike to a real variant. */
  function product(handle) {
    if (!pcache[handle]) {
      pcache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return pcache[handle];
  }

  function excluded(p) {
    return (p.tags || []).some(function (t) { return String(t).toLowerCase() === 'no-trade'; });
  }

  /* The anchor: the variant whose price the row actually prints as its "from" figure, and
     that variant's OWN compare-at. This is a line-for-line port of snippets/nsi-price-anchor.liquid
     so the client-side rows and the Liquid cards cannot disagree - same tie-break (a strictly
     lower price wins, so equal-priced variants keep the earliest), no filtering on
     availability (product.price is the minimum across ALL variants, including sold-out ones,
     so filtering would stop the anchor matching the printed price), and a nil compare-at
     coerced to 0 before any comparison. Returns null when there is nothing to anchor to. */
  function anchor(p) {
    var vs = p.variants || [];
    var price = 0, compare = 0, found = false, i, vp, vc;
    for (i = 0; i < vs.length; i++) {
      vp = typeof vs[i].price === 'number' ? vs[i].price : 0;
      vc = typeof vs[i].compare_at_price === 'number' ? vs[i].compare_at_price : 0;
      if (!found || vp < price) {
        found = true;
        price = vp;
        compare = vc > vp ? vc : 0;
      }
    }
    if (!found) return null;
    return { price: price, compare: compare, onSale: compare > price };
  }

  /* ---- markup ------------------------------------------------------------ */
  /* Element for element the same as snippets/product-price.liquid lines 96-112, so the
     colours in nsi-site.css apply here with no second set of rules to drift. */
  function tradeLine(o) {
    var root = el('span', 'nsi-trade-price' + (o.onSale ? '' : ' nsi-trade-price--nosale'));
    root.setAttribute('data-nsi-trade', '');
    root.setAttribute('data-nsi-rate', o.rate);

    var compare = el('s', 'nsi-trade-price__compare');
    compare.setAttribute('data-nsi-trade-compare', '');
    if (o.onSale) { compare.textContent = money(o.compareAt); } else { compare.hidden = true; }
    root.appendChild(compare);

    root.appendChild(el('s', 'nsi-trade-price__was', (o.varies ? FROM : '') + money(o.price)));

    var slash = el('span', 'nsi-trade-price__slash', '/');
    slash.setAttribute('aria-hidden', 'true');
    root.appendChild(slash);

    var now = el('span', 'nsi-trade-price__now', (o.varies ? FROM : '') + money(o.trade));
    now.setAttribute('data-nsi-trade-now', '');
    root.appendChild(now);

    root.appendChild(el('span', 'nsi-trade-price__badge', o.badge));
    return root;
  }

  /* The retail line. Same two figures and the same two colours the collection cards use
     (nsi-collection.css: muted struck compare-at, then #19B45B on the current price only
     when there is something to compare it against). A full price stays dark. */
  function retailLine(o) {
    var root = el('span', 'nsi-live-price' + (o.onSale ? ' nsi-live-price--sale' : ''));
    root.setAttribute('data-nsi-live-price', '');
    if (o.onSale) root.appendChild(el('s', 'nsi-live-price__compare', money(o.compareAt)));
    root.appendChild(el('span', 'nsi-live-price__now', (o.varies ? FROM : '') + money(o.price)));
    return root;
  }

  /* ---- fitting ----------------------------------------------------------- */
  /* A result row's text column is roughly 220px once the 52px thumbnail and the gaps are
     taken out, and all five parts of a trade line do not always fit. The compare-at goes
     first (it is the least useful of the three to a trade buyer, who is comparing against
     today's retail, not last season's), then the whole line steps down a size. Measured
     rather than guessed at a breakpoint, because the same markup renders in the desktop
     dropdown, the tablet two-column grid and the full-width mobile takeover. */
  function fit(line) {
    line.classList.remove('nsi-live-fit-tight', 'nsi-live-fit-tighter');
    if (!line.clientWidth) return;
    if (line.scrollWidth <= line.clientWidth + 1) return;
    line.classList.add('nsi-live-fit-tight');
    if (line.scrollWidth > line.clientWidth + 1) line.classList.add('nsi-live-fit-tighter');
  }
  function refitAll() {
    lines = lines.filter(function (l) { return l.isConnected; });
    lines.forEach(fit);
  }
  var refitTimer;
  window.addEventListener('resize', function () {
    clearTimeout(refitTimer);
    refitTimer = setTimeout(refitAll, 120);
  });

  /* ---- row upgrade ------------------------------------------------------- */
  function upgrade(row) {
    if (row.getAttribute('data-nsi-price-done')) return;
    var priceEl = row.querySelector('.nsi-live-item-price');
    if (!priceEl) return;
    var handle = handleOf(row.getAttribute('href'));
    if (!handle) return;
    row.setAttribute('data-nsi-price-done', '1');

    product(handle).then(function (p) {
      if (!p) return;                                /* unreadable, leave the plain price */
      var a = anchor(p);
      if (!a || !(a.price > 0)) return;

      var price = a.price;
      var o = {
        price: price,
        compareAt: a.compare,
        onSale: a.onSale,
        varies: p.price_varies === true || (typeof p.price_max === 'number' && p.price_max > price)
      };

      var st = tradeState();
      var line;
      if (st && !excluded(p)) {
        var trade = tradeCents(price, st.rate);
        /* a "discount" that lands on the price itself is noise, not a trade rate */
        if (trade > 0 && trade < price) {
          o.rate = st.rate;
          o.badge = st.badge;
          o.trade = trade;
          line = tradeLine(o);
        }
      }
      if (!line) {
        /* Nothing to say beyond the figure already on screen. Leave the renderer's node
           alone rather than swapping one dark price for an identical dark price. */
        if (!o.onSale && !o.varies) return;
        line = retailLine(o);
      }

      /* The plain price is superseded, not deleted: same class the Liquid price wrapper
         carries, hidden by the one rule in nsi-site.css. */
      priceEl.classList.add('nsi-price--superseded');
      priceEl.parentNode.insertBefore(line, priceEl.nextSibling);
      lines.push(line);
      if (window.requestAnimationFrame) { requestAnimationFrame(function () { fit(line); }); } else { fit(line); }
    });
  }

  function scan(scope) {
    var rows = (scope || document).querySelectorAll('a.nsi-live-item:not([data-nsi-price-done])');
    for (var i = 0; i < rows.length; i++) upgrade(rows[i]);
  }

  /* ---- wiring ------------------------------------------------------------ */
  function bind() {
    var boxes = document.querySelectorAll('[data-nsi-live]');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      if (box.__nsiPriceBound) continue;
      box.__nsiPriceBound = 1;
      if (window.MutationObserver) {
        new MutationObserver(function (recs) {
          for (var j = 0; j < recs.length; j++) {
            if (recs[j].addedNodes.length) { scan(recs[j].target); break; }
          }
        }).observe(box, { childList: true, subtree: true });
      }
      scan(box);
    }
  }

  if (window.fetch && window.Promise) {
    bind();
    document.addEventListener('DOMContentLoaded', bind);
    setTimeout(bind, 1500);
  }

  /* The hook sections/static-basic-header.liquid documents. It is not required - the
     observer above catches rows either way - but if the renderer is ever changed to call
     it, the node it gets back behaves identically and is never upgraded twice. */
  window.nsiLiveTradePrice = function (pr) {
    return el('span', 'nsi-live-item-price', '$' + pr.price);
  };

  window.NSISearchTradePrice = { scan: scan, refit: refitAll, bind: bind };
})();
